import { useState } from "react";
import {
  InsuranceDashboardHeader,
  InsuranceSummarySection,
  InsuranceRevenueOverviewSection,
  InsuranceRankingsSection,
  InsuranceBreakdownsSection,
  InsuranceRevenueStreamsSection,
  getInsuranceSummaryCards,
  getInsuranceTrendData,
  getInsurancePolicyTypeBreakdown,
  getInsuranceUserTypeBreakdown,
} from "../../../components/reportDashboard/insurance/insurance";
import {
  useInsuranceSummaryMetrics,
  useInsuranceRevenueStreams,
  useInsuranceRevenueTrends,
  useInsuranceBreakdowns,
  useInsuranceRankings,
} from "../../../hooks/useReportDashboard";
import { getDateRangeFromFilter } from "../../../utils/reportDashboard/reportDashboardUtils";
import { getErrorMessage } from "../../../utils/error";

export function Insurance() {
  const [timeRange, setTimeRange] = useState("monthly");
  const dateRange = getDateRangeFromFilter(timeRange);

  const {
    data: summaryData,
    isLoading: summaryLoading,
    isError: summaryError,
    error: summaryErrObj,
    refetch: summaryRefetch,
  } = useInsuranceSummaryMetrics(dateRange);

  const {
    data: trendsData,
    isLoading: trendsLoading,
    isError: trendsError,
    error: trendsErrObj,
    refetch: trendsRefetch,
  } = useInsuranceRevenueTrends(dateRange);

  const {
    data: streamsData,
    isLoading: streamsLoading,
    isError: streamsError,
    error: streamsErrObj,
    refetch: streamsRefetch,
  } = useInsuranceRevenueStreams(dateRange);

  const {
    data: breakdownsData,
    isLoading: breakdownsLoading,
    isError: breakdownsError,
    error: breakdownsErrObj,
    refetch: breakdownsRefetch,
  } = useInsuranceBreakdowns(dateRange);

  const {
    data: rankingsData,
    isLoading: rankingsLoading,
    isError: rankingsError,
    error: rankingsErrObj,
    refetch: rankingsRefetch,
  } = useInsuranceRankings(dateRange);

  const summaryState = {
    loading: summaryLoading,
    error: summaryError,
    message: getErrorMessage(summaryErrObj),
    onRetry: () => summaryRefetch(),
  };

  const trendsState = {
    loading: trendsLoading,
    error: trendsError,
    message: getErrorMessage(trendsErrObj),
    onRetry: () => trendsRefetch(),
  };

  const streamsState = {
    loading: streamsLoading,
    error: streamsError,
    message: getErrorMessage(streamsErrObj),
    onRetry: () => streamsRefetch(),
  };

  const breakdownsState = {
    loading: breakdownsLoading,
    error: breakdownsError,
    message: getErrorMessage(breakdownsErrObj),
    onRetry: () => breakdownsRefetch(),
  };

  const rankingsState = {
    loading: rankingsLoading,
    error: rankingsError,
    message: getErrorMessage(rankingsErrObj),
    onRetry: () => rankingsRefetch(),
  };

  const summaryCards = getInsuranceSummaryCards(
    summaryData?.data?.summaryMetrics,
  );
  const monthlyTrend = getInsuranceTrendData(trendsData?.data?.revenueTrends);
  const policyTypeBreakdown = getInsurancePolicyTypeBreakdown(breakdownsData);
  const revenueByUserType = getInsuranceUserTypeBreakdown(breakdownsData);
  const revenueBySupplier = breakdownsData?.data?.revenueBySupplier || [];
  const topDestinations = rankingsData?.data?.topDestinations || [];
  const topPolicies = rankingsData?.data?.topPolicies || [];
  const revenueStreams = streamsData?.data?.revenueStreams || [];

  return (
    <div className="space-y-6">
      <InsuranceDashboardHeader
        timeRange={timeRange}
        onTimeRangeChange={setTimeRange}
      />

      <InsuranceSummarySection
        state={summaryState}
        summaryCards={summaryCards}
      />

      <InsuranceRevenueOverviewSection
        trendsState={trendsState}
        breakdownsState={breakdownsState}
        monthlyTrend={monthlyTrend}
        policyTypeBreakdown={policyTypeBreakdown}
      />

      <InsuranceRankingsSection
        state={rankingsState}
        topDestinations={topDestinations}
        topPolicies={topPolicies}
      />

      <InsuranceBreakdownsSection
        state={breakdownsState}
        revenueByUserType={revenueByUserType}
        revenueBySupplier={revenueBySupplier}
      />

      <InsuranceRevenueStreamsSection
        state={streamsState}
        revenueStreams={revenueStreams}
      />
    </div>
  );
}
